/**
 * AI API Client with JWT Refresh
 *
 * Separate axios instance for AI endpoints (/api/ai/...) with:
 * - Proactive JWT token refresh (60s buffer)
 * - Retry-once on 401 with token_not_valid
 * - Token expiry checking and queue management
 *
 * Used by: FroggerAI, FroggyAI advisor
 */

import axios from 'axios';
import { getAiBaseUrl } from './utils/aiBaseUrl';
import { STREAM_SILENCE_MS } from './services/streamWatchdog';
import { newTurnId } from './utils/turnId';

// Get CSRF token from cookies
function getCSRFToken() {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Ensure URL ends with trailing slash
function ensureTrailingSlash(url) {
  if (url && !url.endsWith('/')) {
    return url + '/';
  }
  return url;
}

// Create AI-specific axios instance with JWT refresh
// Uses environment-aware base URL (no hard-coded hostnames)
// baseURL is host-only; request paths include /api prefix
const baseURL = getAiBaseUrl().replace(/\/+$/, ''); // Remove trailing slashes

export const aiApi = axios.create({
  baseURL,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
});

// Dev-only logging for base URL selection
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.debug('[FroggerAI] baseURL:', aiApi.defaults.baseURL);

  // Runtime assertion: baseURL should be host-only (no path segments)
  try {
    const urlObj = new URL(aiApi.defaults.baseURL);
    if (urlObj.pathname && urlObj.pathname !== '/') {
      console.warn('[FroggerAI] WARNING: baseURL contains path:', urlObj.pathname, '- should be host-only');
    }
  } catch (e) {
    console.warn('[FroggerAI] WARNING: Invalid baseURL:', aiApi.defaults.baseURL);
  }
}

// ============================================================================
// JWT Token Utilities
// ============================================================================

/**
 * Decode JWT token payload without verification (client-side only)
 * @param {string} token - JWT access token
 * @returns {object|null} Decoded payload or null if invalid
 */
const decodeJWT = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (e) {
    console.warn('[Auth] Failed to decode JWT:', e.message);
    return null;
  }
};

/**
 * Get access token expiration timestamp
 * @returns {number|null} Unix timestamp (seconds) or null if no valid token
 */
export const getAccessTokenExpiry = () => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  const payload = decodeJWT(token);
  return payload?.exp || null;
};

/**
 * Check if access token is expiring soon
 * @param {number} bufferSeconds - Seconds before expiry to consider "expiring soon" (default: 60s for clock skew)
 * @returns {boolean} True if token expires within buffer window
 */
export const isAccessTokenExpiringSoon = (bufferSeconds = 60) => {
  const exp = getAccessTokenExpiry();
  if (!exp) return true; // No token = treat as expired
  const nowSeconds = Math.floor(Date.now() / 1000);
  return (exp - nowSeconds) < bufferSeconds;
};

/**
 * Get tokens from localStorage
 */
export const getAccessToken = () => localStorage.getItem('authToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');

/**
 * Set tokens in localStorage and axios headers
 */
export const setTokens = ({ access, refresh }) => {
  if (access) {
    aiApi.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    localStorage.setItem('authToken', access);
  }
  if (refresh) {
    localStorage.setItem('refreshToken', refresh);
  }
};

/**
 * Clear tokens and logout
 */
export const logoutAndClear = () => {
  delete aiApi.defaults.headers.common['Authorization'];
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('facilityUserRoles');
  localStorage.removeItem('isEmailVerified');
};

// Track if a refresh is already in progress to prevent concurrent refresh attempts
let refreshInProgress = false;
let refreshSubscribers = [];

/**
 * Add request to queue waiting for token refresh
 */
const subscribeToRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all queued requests that refresh completed
 */
const onRefreshComplete = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

/**
 * Helper to check if path is public (doesn't require auth)
 */
const isPublicPath = (path) => {
  const publicPaths = ['/login', '/signup', '/reset-password', '/verify-email', '/public', '/'];
  return publicPaths.some(p => path.startsWith(p));
};

/**
 * Attempt to refresh JWT access token using refresh token
 * @returns {Promise<string>} New access token
 * @throws Error if refresh fails
 */
const attemptTokenRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  console.log('[Auth][Refresh] Attempting token refresh...');
  try {
    const response = await aiApi.post('/api/auth/token/refresh/', {
      refresh: refreshToken
    });

    const { access, refresh: newRefresh } = response.data;

    // Store new tokens (handles ROTATE_REFRESH_TOKENS=True)
    setTokens({ access, refresh: newRefresh || refreshToken });

    console.log('[Auth][Refresh] Token refreshed successfully');
    return access;
  } catch (error) {
    console.error('[Auth][Refresh] Failed:', error.response?.data || error.message);
    throw error;
  }
};

// ============================================================================
// Request Interceptor - Proactive Token Refresh
// ============================================================================

let aiRequestCounter = 0;

// Helper to check if cookie exists
const hasCookie = (name) => {
  if (!document.cookie) return false;
  return document.cookie.split(';').some(c => c.trim().startsWith(name + '='));
};

aiApi.interceptors.request.use(
  async (config) => {
    aiRequestCounter++;
    const reqNum = aiRequestCounter;

    // Skip refresh check for the refresh endpoint itself
    if (config.url?.includes('auth/token/refresh')) {
      console.log(`[AI API][REQ #${reqNum}] Skipping auth for refresh endpoint`);
      return config;
    }

    // Always read the latest token from localStorage at request time (not at module load)
    let currentToken = getAccessToken();

    // Check if token is expiring soon (60s buffer for clock skew)
    if (isAccessTokenExpiringSoon(60)) {
      console.log(`[AI API][REQ #${reqNum}] Token expiring soon, refreshing...`);
      if (!refreshInProgress) {
        refreshInProgress = true;
        try {
          const newToken = await attemptTokenRefresh();
          refreshInProgress = false;
          onRefreshComplete(newToken);
          currentToken = newToken;
        } catch (error) {
          refreshInProgress = false;
          onRefreshComplete(null);
          console.error(`[AI API][REQ #${reqNum}] Refresh failed, proceeding without token`);
        }
      } else {
        // Refresh already in progress, wait for it
        const newToken = await new Promise((resolve) => {
          subscribeToRefresh(resolve);
        });
        if (newToken) currentToken = newToken;
      }
    }

    // CRITICAL: Always attach Authorization header if token exists
    if (currentToken) {
      config.headers['Authorization'] = `Bearer ${currentToken}`;
    }

    // Always set X-Requested-With header
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Add CSRF token for state-changing methods
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }

    // Ensure trailing slash on URL
    config.url = ensureTrailingSlash(config.url);

    // Decode JWT for claim inspection
    let jwtFlags = null;
    if (currentToken) {
      try {
        const parts = currentToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          jwtFlags = {
            hasUserId: !!payload.user_id,
            hasEmail: !!payload.email,
            hasIss: !!payload.iss,
            hasAud: !!payload.aud,
            hasExp: !!payload.exp,
            hasFacilityRoles: !!payload.facility_roles || !!payload.facilityRoles,
          };
        }
      } catch (e) {
        jwtFlags = { error: 'Failed to decode JWT' };
      }
    }

    // UNCONDITIONAL LOGGING for all AI requests
    console.log(`[AI API][REQ #${reqNum}] ========================================`);
    console.log(`[AI API][REQ #${reqNum}] URL: ${config.method?.toUpperCase()} ${aiApi.defaults.baseURL}${config.url}`);
    console.log(`[AI API][REQ #${reqNum}] withCredentials: ${config.withCredentials}`);
    console.log(`[AI API][REQ #${reqNum}] Headers Present:`, {
      Authorization: !!config.headers['Authorization'],
      'X-CSRFToken': !!config.headers['X-CSRFToken'],
      'X-Requested-With': !!config.headers['X-Requested-With'],
    });
    console.log(`[AI API][REQ #${reqNum}] Auth Token:`, currentToken ? 'present' : 'MISSING');
    if (currentToken) {
      console.log(`[AI API][REQ #${reqNum}] Token Preview:`, currentToken.substring(0, 30) + '...');
    }
    console.log(`[AI API][REQ #${reqNum}] JWT Flags:`, jwtFlags || 'token not decoded');
    console.log(`[AI API][REQ #${reqNum}] Cookies Present:`, {
      csrftoken: hasCookie('csrftoken'),
      sessionid: hasCookie('sessionid'),
    });

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Response Interceptor - Retry Once on 401
// ============================================================================

aiApi.interceptors.response.use(
  (response) => {
    // Log successful responses for AI requests
    if (response.config.url?.includes('/ai/')) {
      console.log(`[AI API][RES] ${response.config.method?.toUpperCase()} ${response.config.url} → ${response.status} OK`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // UNCONDITIONAL LOGGING for 401/403 errors on AI requests
    if (status === 401 || status === 403) {
      console.error(`[AI API][RES] ⚠️ ${status} ${error.config.method?.toUpperCase()} ${error.config.url}`);
      console.error(`[AI API][RES] Response:`, error.response?.data);

      // Check what's missing
      const missing = [];
      if (!error.config.headers['Authorization']) missing.push('Authorization');
      if (!error.config.headers['X-CSRFToken'] && ['post', 'put', 'patch', 'delete'].includes(error.config.method?.toLowerCase())) {
        missing.push('X-CSRFToken');
      }

      const token = getAccessToken();
      if (token) {
        const payload = decodeJWT(token);
        if (payload && !payload.facility_roles && !payload.facilityRoles) {
          missing.push('JWT:facility_roles');
        }
      } else {
        missing.push('authToken');
      }

      console.error(`[AUTH FAIL] ${error.config.method?.toUpperCase()} ${error.config.url} — missing: ${missing.join(', ') || 'nothing (check backend permissions)'}`);
    }

    // Check if this is a 401 with token errors
    const is401 = error.response?.status === 401;
    const isTokenInvalid =
      error.response?.data?.code === 'token_not_valid' ||
      error.response?.data?.detail?.includes('Token is expired') ||
      error.response?.data?.message?.includes('Token is expired');

    // Check if 401 is due to missing Authorization header (cold start race)
    const isNoCredentials =
      error.response?.data?.detail?.includes('Authentication credentials were not provided') ||
      error.response?.data?.detail?.includes('credentials were not provided');

    // Only retry once per request for token errors OR missing credentials
    if (is401 && (isTokenInvalid || isNoCredentials) && !originalRequest._retryAttempted) {
      originalRequest._retryAttempted = true;

      // Special case: missing credentials (cold start race) - retry immediately without refresh
      if (isNoCredentials) {
        console.log('[Auth][Retry] 401 "no credentials" - retrying immediately with current token...');
        const currentToken = getAccessToken();
        if (currentToken) {
          originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
          console.log('[Auth][Retry] Injected missing Authorization header, retrying...');
          return aiApi.request(originalRequest);
        } else {
          console.error('[Auth][Retry] No token available for retry, failing request');
          return Promise.reject(error);
        }
      }

      // Token invalid/expired - attempt refresh
      console.log('[Auth][Retry] 401 token invalid, attempting refresh and retry...');

      // If refresh already in progress, wait for it
      if (refreshInProgress) {
        const newToken = await new Promise((resolve) => {
          subscribeToRefresh(resolve);
        });
        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return aiApi.request(originalRequest);
        }
      }

      // Attempt refresh
      refreshInProgress = true;
      try {
        const newToken = await attemptTokenRefresh();
        refreshInProgress = false;
        onRefreshComplete(newToken);

        // Retry original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        console.log('[Auth][Retry] Retrying original request with fresh token...');
        return aiApi.request(originalRequest);
      } catch (refreshError) {
        refreshInProgress = false;
        onRefreshComplete(null);

        // Refresh failed - clear tokens and redirect to login
        console.error('[Auth][Retry] Token refresh failed, logging out');
        logoutAndClear();

        // Only redirect if on a protected path
        const currentPath = window.location.pathname;
        if (!isPublicPath(currentPath)) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other error statuses
    if (error.response?.status === 401) {
      console.warn('[AI API] 401 Unauthorized - Authentication required');
    } else if (error.response?.status === 403) {
      console.warn('[AI API] 403 Forbidden - Insufficient permissions');
    } else if (error.response?.status === 404) {
      console.warn('[AI API] 404 Not Found - Check endpoint URL');
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Initialize Auth Token from localStorage
// ============================================================================

// NOTE: Authorization header is injected dynamically in the request interceptor
// to ensure it always reads the latest token from localStorage at request time.
// This prevents cold-start races where the module loads before tokens are available.

// ============================================================================
// AI Endpoints
// ============================================================================

/**
 * AI endpoints using JWT-authenticated aiApi client
 * NOTE: AI endpoints get facility from URL path, NOT from X-Facility-Id header
 */
export const ai = {
  /**
   * Ask FroggyAI a question (customer-facing advisor)
   */
  ask: async (facilityId, payload, facilityName) => {
    if (!facilityId) {
      return Promise.reject(new Error('No facility selected. Please open a facility and try again.'));
    }

    console.debug('[AI][REQ DEBUG] ask request:', {
      facilityId,
      facilityName,
      baseURL: aiApi.defaults.baseURL,
      withCreds: aiApi.defaults.withCredentials
    });

    try {
      const response = await aiApi.post(`/api/ai/${facilityId}/ask/`, payload, {
        headers: facilityName ? { 'x-facility-name': facilityName } : undefined
      });
      console.debug('[AI][REQ DEBUG] ask response:', response.status);
      return response;
    } catch (error) {
      // Interceptor handles 401 retry automatically
      console.debug('[AI][REQ DEBUG] ask error:', {
        status: error.response?.status,
        requestId: error.response?.headers?.['x-request-id'],
        detail: error.response?.data?.detail
      });
      throw error;
    }
  },

  /**
   * Get FroggerAI facility summary (internal keeper tool)
   */
  summary: async (facilityId, facilityName) => {
    if (!facilityId) {
      return Promise.reject(new Error('No facility selected. Please open a facility and try again.'));
    }

    console.debug('[AI][REQ DEBUG] summary request:', {
      facilityId,
      facilityName,
      baseURL: aiApi.defaults.baseURL,
      withCreds: aiApi.defaults.withCredentials
    });

    try {
      const response = await aiApi.get(`/api/ai/${facilityId}/summary/`, {
        headers: facilityName ? { 'x-facility-name': facilityName } : undefined
      });
      console.debug('[AI][REQ DEBUG] summary response:', response.status);
      return response;
    } catch (error) {
      // Interceptor handles 401 retry automatically
      console.debug('[AI][REQ DEBUG] summary error:', {
        status: error.response?.status,
        requestId: error.response?.headers?.['x-request-id'],
        detail: error.response?.data?.detail
      });
      throw error;
    }
  }
};

/**
 * Get AI summary stats (legacy compatibility)
 */
export const getSummaryStats = async (facilityId) => {
  if (!facilityId) {
    return Promise.reject(new Error('No facility selected. Please open a facility and try again.'));
  }
  const response = await aiApi.get(`/api/ai/${facilityId}/summary/`);
  return response.data;
};

/**
 * Get AI summary (legacy compatibility)
 */
export function getAISummary(facilityId, options = {}) {
  if (!facilityId) {
    return Promise.reject(new Error('No facility selected. Please open a facility and try again.'));
  }
  return aiApi.get(`/api/ai/${facilityId}/summary/`, options);
}

/**
 * THE TURN LOG (I-03 R6 · S3-06.4).
 *
 * Cowork QA of R6: the turn log was unverifiable without Django admin.
 * `GET /api/ai/<facility>/turns/` answers the last N turns for an Owner
 * or Manager — what was said, which tools ran, how many rows it wrote,
 * how long it took, and what went wrong.
 *
 * `before` is a KEYSET cursor (`created_at` of the last row on the page),
 * passed through `params` so axios URL-encodes the `+` in the timestamp;
 * a hand-built query string reads that as a space and the server answers
 * 400 `bad_before` rather than paging from the wrong moment.
 *
 * @param {string} facilityId
 * @param {{limit?: number, before?: string}} options
 */
export function getFroggerTurns(facilityId, { limit = 50, before } = {}) {
  if (!facilityId) {
    return Promise.reject(new Error('No facility selected.'));
  }
  const params = { limit };
  if (before) params.before = before;
  return aiApi.get(`/api/ai/${facilityId}/turns/`, { params });
}

/**
 * Send voice message to FroggerAI
 * @param {string} facilityId - Facility UUID
 * @param {Blob} audioBlob - Audio recording blob
 * @param {number} duration - Recording duration in seconds
 * @param {string} conversationId - Conversation ID for session continuity (CRITICAL)
 * @returns {Promise} - {transcript, reply, usage}
 */
export async function sendVoiceMessage(facilityId, audioBlob, duration, conversationId = null, place = null, turnId = null, stoppedTurn = null) {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) {
    throw new Error('Authentication required');
  }

  // Defensive checks
  if (!audioBlob || audioBlob.size === 0) {
    throw new Error('No audio data to send');
  }

  if (!duration || duration <= 0) {
    throw new Error('Invalid recording duration');
  }

  // Create FormData for multipart/form-data
  const formData = new FormData();
  // The extension follows the BLOB, not a guess: conversation mode
  // sends `audio/wav` (the detector's own samples), tap-to-record sends
  // whatever `MediaRecorder.isTypeSupported` allowed — `audio/mp4` on
  // iOS. Whisper dispatches on the filename as well as the bytes, so a
  // WAV called `.webm` is a transcription that fails for no visible
  // reason.
  const ext = (audioBlob.type || '').includes('wav') ? 'wav'
    : (audioBlob.type || '').includes('mp4') ? 'm4a'
      : (audioBlob.type || '').includes('ogg') ? 'ogg' : 'webm';
  formData.append('audio', audioBlob, `voice-${Date.now()}.${ext}`);

  // CRITICAL: Include conversation ID for session continuity between voice and typed messages
  if (conversationId) {
    formData.append('cid', conversationId);
  }

  // I-03 R6 / S3-06.3 — WHERE THE KEEPER IS, on a voice turn too.
  //
  // A segment spoken standing at rack 3 has to mean what "feed tank two"
  // typed there means. FormData cannot carry a nested object, so it goes
  // as a JSON STRING and the backend's `_parse_place` — the SAME
  // function the streaming endpoint uses — parses it.
  //
  // Omitted when there is no place: the backend reads a missing field as
  // "the caller said nothing about place" (keep the sticky one) and an
  // empty object as "nowhere", and a voice turn must not silently wipe
  // the rack the dock set two taps ago.
  // Item 10: an EXPLICIT `{}` is "nowhere" (the keeper tapped ×) and is
  // sent; `null` — no place — still sends no field.
  if (place && typeof place === 'object') {
    formData.append('place', JSON.stringify(place));
  }

  // ⚠️ ONE TURN, ONE RUN — the turn's one-time id, minted ONCE per spoken
  // turn by the caller (or here). The server claims it before Whisper
  // (`frogger_ai/langgraph/turn_claim.py`): a silently retried upload
  // neither transcribes nor runs a second time.
  formData.append('turn_id', turnId || newTurnId());

  // A STOPPED TURN REMEMBERS ONLY WHAT THE SCREEN SHOWED (Fable brief
  // 2026-10-23, item E). A voice turn has no Stop of its own, but the
  // request AFTER a stopped typed turn may be spoken, and it carries the
  // report the same way the stream does (`streamingAPI.stoppedTurnParam`).
  // A POST body has no URL budget, so only the 2,000-character cap applies.
  if (stoppedTurn && stoppedTurn.turn_id) {
    const whole = String(stoppedTurn.shown_text || '').trim();
    const nothingShown = !whole || !!stoppedTurn.nothing_shown;
    formData.append('stopped_turn', JSON.stringify({
      turn_id: String(stoppedTurn.turn_id),
      shown_text: nothingShown ? ''
        : (whole.length > 2000 ? `${whole.slice(0, 2000).trimEnd()}…` : whole),
      nothing_shown: nothingShown,
    }));
  }

  const options = {
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': undefined,  // Delete default application/json, let browser set multipart boundary
      'X-Facility-Id': facilityId
    },
    // ⚠️ THE SAME 120 s THE STREAM'S WATCHDOG USES (run 13, F1), and it
    // is a RAISE, never a tightening: the 90 s beta AI floor governs the
    // model calls INSIDE the turn, not the request the browser is
    // holding open, and a voice turn is Whisper AND the graph AND the
    // tools. The panel turns a timeout here into the same visible line a
    // stalled SSE turn gets, rather than a spinner nothing ends.
    timeout: STREAM_SILENCE_MS
  };

  return aiApi.post(`/api/ai/${facilityId}/voice/`, formData, options);
}

/**
 * Get voice message configuration limits from backend (FroggerAI - internal)
 * @returns {Promise} - {max_duration_seconds, recommended_max_seconds, max_file_size_mb}
 */
export async function getVoiceConfig() {
  return aiApi.get('/api/ai/voice/config/');
}

/**
 * Send voice message to FroggyAI (customer-facing)
 * @param {string} facilityId - Facility UUID
 * @param {Blob} audioBlob - Audio blob from MediaRecorder
 * @param {number} duration - Recording duration in seconds
 * @returns {Promise} - {transcript, answer, citations, usage}
 */
export async function sendFroggyVoiceMessage(facilityId, audioBlob, duration) {
  // Defensive checks
  if (!audioBlob || audioBlob.size === 0) {
    throw new Error('No audio data to send');
  }

  if (!duration || duration <= 0) {
    throw new Error('Invalid recording duration');
  }

  // Create FormData for multipart/form-data
  const formData = new FormData();
  formData.append('audio', audioBlob, `froggy-voice-${Date.now()}.webm`);

  const options = {
    headers: {
      'Content-Type': undefined,  // Let browser set multipart boundary
    },
    timeout: 90000  // 90s timeout for voice (Whisper + completion)
  };

  // Add facility ID header if provided
  if (facilityId) {
    options.headers['X-Facility-Id'] = facilityId;
  }

  // Use facility-scoped URL if we have facility ID, otherwise use base URL
  const url = facilityId
    ? `/api/facility/${facilityId}/advisor/froggy/voice/`
    : '/api/facility/advisor/froggy/voice/';

  return aiApi.post(url, formData, options);
}

/**
 * Get voice configuration for FroggyAI (customer-facing)
 * @returns {Promise} - {max_duration_seconds, recommended_max_seconds, max_file_size_mb}
 */
export async function getFroggyVoiceConfig() {
  return aiApi.get('/api/facility/advisor/froggy/voice/config/');
}

/**
 * Export AI client for direct usage
 */
export const getAiClient = () => aiApi;

/**
 * Legacy compatibility - export aiClient
 */
export const aiClient = aiApi;
